import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  storeGetOrDefault, storeSet,
  DEF_HOW_IT_WORKS, DEF_COMPANY_TRUST, DEF_CONTACT_CTA, DEF_HOME_SHOWCASE, DEF_HOME_VIDEOS,
} from '../../store';
import '../admin.css';

const Toast = ({ msg, type }) => msg ? <div className={`adm-toast adm-toast-${type}`}>{msg}</div> : null;

// ── image upload field (URL or file upload) ───────────────────────────────────
const ImageUploadField = ({ label, value, onChange, placeholder = 'https://...' }) => {
  const fileRef = React.useRef();
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="adm-form-group">
      <label className="adm-label">{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="adm-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1 }}
        />
        <span style={{ color: '#9ca3af', fontSize: 12, whiteSpace: 'nowrap' }}>or</span>
        <button
          type="button"
          className="adm-btn adm-btn-secondary"
          style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}
          onClick={() => fileRef.current.click()}
        >
          📁 Upload
        </button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      </div>
      {value && (
        <img src={value} alt="preview" style={{ marginTop: 8, maxWidth: 220, height: 110, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
      )}
    </div>
  );
};

// ── tiny link card ────────────────────────────────────────────────────────────
const QuickLink = ({ to, label, desc }) => (
  <div className="adm-settings-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 20px' }}>
    <div>
      <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{label}</div>
      <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>{desc}</div>
    </div>
    <Link to={to} className="adm-btn adm-btn-secondary" style={{ whiteSpace: 'nowrap', fontSize: 13, padding: '6px 14px' }}>
      Edit →
    </Link>
  </div>
);

// ── HomeAdmin ─────────────────────────────────────────────────────────────────
const HomeAdmin = () => {
  const [hiw,       setHiw]       = useState(() => storeGetOrDefault('howItWorks',   DEF_HOW_IT_WORKS));
  const [ct,        setCt]        = useState(() => storeGetOrDefault('companyTrust', DEF_COMPANY_TRUST));
  const [cta,       setCta]       = useState(() => storeGetOrDefault('contactCTA',   DEF_CONTACT_CTA));
  const [showcase,  setShowcase]  = useState(() => storeGetOrDefault('homeShowcase', DEF_HOME_SHOWCASE));
  const [videos,    setVideos]    = useState(() => {
    const saved = storeGetOrDefault('homeVideos', DEF_HOME_VIDEOS);
    const base = Array.isArray(saved) ? saved.slice(0, 3) : DEF_HOME_VIDEOS;
    while (base.length < 3) {
      base.push({ id: Date.now() + base.length, title: '', url: '' });
    }
    return base;
  });
  const [toast,     setToast]     = useState({ msg: '', type: 'success' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  // ── How It Works helpers ─────────────────────────────────────────────────
  const setHiwF = (k) => (e) => setHiw(h => ({ ...h, [k]: e.target.value }));
  const saveHiw = () => { storeSet('howItWorks', hiw); showToast('"How It Works" saved!'); };

  // ── Company Trust helpers ─────────────────────────────────────────────────
  const setJgi  = (k) => (e) => setCt(c => ({ ...c, jgi:  { ...c.jgi,  [k]: e.target.value } }));
  const setJgzt = (k) => (e) => setCt(c => ({ ...c, jgzt: { ...c.jgzt, [k]: e.target.value } }));
  const setJgiBullet = (idx) => (e) => setCt(c => ({
    ...c, jgi: { ...c.jgi, bullets: c.jgi.bullets.map((b, i) => i === idx ? e.target.value : b) }
  }));
  const addBullet    = () => setCt(c => ({ ...c, jgi: { ...c.jgi, bullets: [...c.jgi.bullets, ''] } }));
  const removeBullet = (idx) => setCt(c => ({ ...c, jgi: { ...c.jgi, bullets: c.jgi.bullets.filter((_, i) => i !== idx) } }));

  const setTeamHeading = (e) => setCt(c => ({ ...c, team: { ...c.team, heading: e.target.value } }));
  const setMember = (idx, k) => (e) => setCt(c => ({
    ...c, team: { ...c.team, members: c.team.members.map((m, i) => i === idx ? { ...m, [k]: e.target.value } : m) }
  }));
  const addMember = () => setCt(c => ({
    ...c, team: { ...c.team, members: [...c.team.members, { id: Date.now(), name: '', role: '', bio: '', initials: '', cardBg: '#1a4b7a' }] }
  }));
  const removeMember = (idx) => setCt(c => ({ ...c, team: { ...c.team, members: c.team.members.filter((_, i) => i !== idx) } }));
  const saveCt = () => { storeSet('companyTrust', ct); showToast('Company Trust section saved!'); };

  // ── Contact CTA helpers ───────────────────────────────────────────────────
  const setNews   = (k)   => (e)   => setCta(c => ({ ...c, newsletter: { ...c.newsletter, [k]: e.target.value } }));
  const setCtaF   = (k)   => (e)   => setCta(c => ({ ...c, cta: { ...c.cta, [k]: e.target.value } }));
  const setCtaImg = (val)           => setCta(c => ({ ...c, cta: { ...c.cta, image: val } }));
  const saveCta   = ()              => { storeSet('contactCTA', cta); showToast('Contact CTA saved!'); };

  // ── Showcase helpers ──────────────────────────────────────────────────────
  const setShowF   = (idx, k) => (e) => setShowcase(s => s.map((p, i) => i === idx ? { ...p, [k]: e.target.value } : p));
  const setShowImg = (idx)    => (val) => setShowcase(s => s.map((p, i) => i === idx ? { ...p, image: val } : p));
  const setSpec   = (pIdx, sIdx, k) => (e) => setShowcase(s => s.map((p, i) => i === pIdx
    ? { ...p, specs: p.specs.map((sp, j) => j === sIdx ? { ...sp, [k]: e.target.value } : sp) } : p));
  const toggleSpecBold = (pIdx, sIdx) => setShowcase(s => s.map((p, i) => i === pIdx
    ? { ...p, specs: p.specs.map((sp, j) => j === sIdx ? { ...sp, bold: !sp.bold } : sp) } : p));
  const addSpec    = (pIdx) => setShowcase(s => s.map((p, i) => i === pIdx ? { ...p, specs: [...p.specs, { label: '', value: '', bold: false }] } : p));
  const removeSpec = (pIdx, sIdx) => setShowcase(s => s.map((p, i) => i === pIdx ? { ...p, specs: p.specs.filter((_, j) => j !== sIdx) } : p));
  const addProduct    = () => setShowcase(s => [...s, { id: `product-${Date.now()}`, name: '', subtitle: '', image: '', specs: [] }]);
  const removeProduct = (idx) => { if (window.confirm('Remove this product from the showcase?')) setShowcase(s => s.filter((_, i) => i !== idx)); };
  const saveShowcase  = () => { storeSet('homeShowcase', showcase); showToast('Product Showcase saved!'); };

  // ── Home YouTube videos helpers ──────────────────────────────────────────
  const getYouTubeThumb = (url) => {
    if (!url || typeof url !== 'string') return '';
    try {
      const parsed = new URL(url.trim());
      const host = parsed.hostname.replace('www.', '');
      let videoId = '';
      if (host === 'youtu.be') {
        videoId = parsed.pathname.slice(1).split('/')[0];
      } else if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (parsed.pathname === '/watch') videoId = parsed.searchParams.get('v') || '';
        else if (parsed.pathname.startsWith('/shorts/')) videoId = parsed.pathname.replace('/shorts/', '').split('/')[0];
        else if (parsed.pathname.startsWith('/embed/')) videoId = parsed.pathname.replace('/embed/', '').split('/')[0];
      }
      if (!videoId) return '';
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    } catch { return ''; }
  };

  const setVideoField = (idx, key) => (e) => setVideos(v => v.map((item, i) => i === idx ? { ...item, [key]: e.target.value } : item));
  const saveVideos = () => {
    const payload = videos.slice(0, 3).map((item, idx) => ({
      id: item.id || idx + 1,
      title: item.title || `Video ${idx + 1}`,
      url: item.url || '',
    }));
    storeSet('homeVideos', payload);
    showToast('Home videos saved!');
  };

  return (
    <div className="adm-page">
      <Toast {...toast} />

      {/* Header */}
      <div className="adm-page-hero">
        <div className="adm-page-hero-icon" style={{ background: 'linear-gradient(135deg,#16a34a,#4ade80)' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <div>
          <h1 className="adm-title">Home Page</h1>
          <p className="adm-subtitle">Control every section that appears on the home page</p>
        </div>
      </div>

      {/* ── Section map ──────────────────────────────────────────────────────── */}
      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 20px', marginBottom: 28 }}>
        <div style={{ fontWeight: 700, color: '#15803d', marginBottom: 10, fontSize: 14 }}>🏠 Home Page Sections</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 8, fontSize: 13 }}>
          {[
            ['1. Hero Slider', '/admin/hero'],
            ['2. Stats Bar', '/admin/settings'],
            ['3. Product Showcase', null],
            ['4. How It Works', null],
            ['5. Company Trust & Team', null],
            ['6. YouTube Videos', null],
            ['7. Contact / Newsletter CTA', null],
          ].map(([label, link]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#374151' }}>
              <span style={{ color: '#16a34a' }}>✓</span>
              {link ? <Link to={link} style={{ color: '#2563eb', textDecoration: 'underline' }}>{label}</Link> : label}
            </div>
          ))}
        </div>
      </div>

      {/* ── Quick links for sections managed elsewhere ──────────────────────── */}
      <div className="adm-settings-grid" style={{ marginBottom: 28 }}>
        <QuickLink to="/admin/hero"     label="Hero Slider"     desc="Edit slides, images, captions and CTA buttons" />
        <QuickLink to="/admin/settings" label="Stats Bar"       desc="Edit the 4 numbers shown below the hero (years, projects, clients…)" />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          3. PRODUCT SHOWCASE
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="adm-settings-card" style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="adm-settings-card-title" style={{ margin: 0 }}>📦 Product Showcase (Home Page Range)</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={addProduct} className="adm-btn adm-btn-secondary" style={{ fontSize: 13 }}>+ Add Product</button>
            <button onClick={saveShowcase} className="adm-btn adm-btn-primary" style={{ fontSize: 13 }}>Save Showcase</button>
          </div>
        </div>

        {showcase.map((product, pIdx) => (
          <div key={pIdx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>Product #{pIdx + 1}: {product.name || '(unnamed)'}</div>
              <button onClick={() => removeProduct(pIdx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>✕ Remove</button>
            </div>

            <div className="adm-form" style={{ gap: 12, marginBottom: 12 }}>
              <div className="adm-form-row">
                <div className="adm-form-group">
                  <label className="adm-label">Product ID (URL slug)</label>
                  <input className="adm-input" value={product.id} onChange={setShowF(pIdx, 'id')} placeholder="e.g. zinga" />
                </div>
                <div className="adm-form-group">
                  <label className="adm-label">Name</label>
                  <input className="adm-input" value={product.name} onChange={setShowF(pIdx, 'name')} placeholder="Product name" />
                </div>
              </div>
              <div className="adm-form-group">
                <label className="adm-label">Subtitle / Description</label>
                <textarea className="adm-input adm-textarea" value={product.subtitle} onChange={setShowF(pIdx, 'subtitle')} rows={2} />
              </div>
              <ImageUploadField
                label="Product Image"
                value={product.image}
                onChange={setShowImg(pIdx)}
              />
            </div>

            {/* Specs */}
            <div style={{ background: '#f9fafb', borderRadius: 6, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Specifications</div>
                <button onClick={() => addSpec(pIdx)} className="adm-btn adm-btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}>+ Spec Row</button>
              </div>
              {product.specs.map((spec, sIdx) => (
                <div key={sIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <input className="adm-input" style={{ fontSize: 13, padding: '6px 10px' }} value={spec.label} onChange={setSpec(pIdx, sIdx, 'label')} placeholder="Label" />
                  <input className="adm-input" style={{ fontSize: 13, padding: '6px 10px' }} value={spec.value} onChange={setSpec(pIdx, sIdx, 'value')} placeholder="Value" />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!spec.bold} onChange={() => toggleSpecBold(pIdx, sIdx)} /> Bold
                  </label>
                  <button onClick={() => removeSpec(pIdx, sIdx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          4. HOW IT WORKS
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="adm-settings-card" style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="adm-settings-card-title" style={{ margin: 0 }}>🔬 How It Works</h3>
          <button onClick={saveHiw} className="adm-btn adm-btn-primary" style={{ fontSize: 13 }}>Save</button>
        </div>
        <div className="adm-form" style={{ gap: 14 }}>
          <div className="adm-form-row">
            <div className="adm-form-group">
              <label className="adm-label">Eyebrow (small text above heading)</label>
              <input className="adm-input" value={hiw.eyebrow} onChange={setHiwF('eyebrow')} placeholder="Science of Protection" />
            </div>
            <div className="adm-form-group">
              <label className="adm-label">Heading</label>
              <input className="adm-input" value={hiw.heading} onChange={setHiwF('heading')} placeholder="How does it work?" />
            </div>
          </div>
          {[['para1', 'Paragraph 1'], ['para2', 'Paragraph 2'], ['para3', 'Paragraph 3'], ['paraBottom', 'Paragraph below diagram']].map(([k, label]) => (
            <div className="adm-form-group" key={k}>
              <label className="adm-label">{label}</label>
              <textarea className="adm-input adm-textarea" value={hiw[k]} onChange={setHiwF(k)} rows={3} />
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          5. COMPANY TRUST (JGI + JGZT + Team)
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="adm-settings-card" style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="adm-settings-card-title" style={{ margin: 0 }}>🏢 Company Trust Section</h3>
          <button onClick={saveCt} className="adm-btn adm-btn-primary" style={{ fontSize: 13 }}>Save All</button>
        </div>

        {/* JGI */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 12 }}>About John Galt International</div>
          <div className="adm-form" style={{ gap: 12 }}>
            <div className="adm-form-group">
              <label className="adm-label">Section Heading</label>
              <input className="adm-input" value={ct.jgi.heading} onChange={setJgi('heading')} />
            </div>
            {[['para1', 'Paragraph 1'], ['para2', 'Paragraph 2 (before bullets)'], ['para3', 'Paragraph 3 (after bullets)']].map(([k, lbl]) => (
              <div className="adm-form-group" key={k}>
                <label className="adm-label">{lbl}</label>
                <textarea className="adm-input adm-textarea" value={ct.jgi[k]} onChange={setJgi(k)} rows={2} />
              </div>
            ))}
            <div className="adm-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="adm-label" style={{ marginBottom: 0 }}>Bullet Points</label>
                <button onClick={addBullet} className="adm-btn adm-btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}>+ Add Bullet</button>
              </div>
              {ct.jgi.bullets.map((b, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input className="adm-input" value={b} onChange={setJgiBullet(idx)} />
                  <button onClick={() => removeBullet(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, padding: '0 6px' }}>×</button>
                </div>
              ))}
            </div>
            <div className="adm-form-row">
              <div className="adm-form-group">
                <label className="adm-label">Website Button Label</label>
                <input className="adm-input" value={ct.jgi.websiteLabel} onChange={setJgi('websiteLabel')} />
              </div>
              <div className="adm-form-group">
                <label className="adm-label">Website URL</label>
                <input className="adm-input" value={ct.jgi.websiteUrl} onChange={setJgi('websiteUrl')} placeholder="https://..." />
              </div>
            </div>
          </div>
        </div>

        {/* JGZT */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 12 }}>About John Galt Zinga Technologies</div>
          <div className="adm-form" style={{ gap: 12 }}>
            <div className="adm-form-group">
              <label className="adm-label">Section Heading</label>
              <input className="adm-input" value={ct.jgzt.heading} onChange={setJgzt('heading')} />
            </div>
            {[['para1', 'Paragraph 1'], ['para2', 'Paragraph 2']].map(([k, lbl]) => (
              <div className="adm-form-group" key={k}>
                <label className="adm-label">{lbl}</label>
                <textarea className="adm-input adm-textarea" value={ct.jgzt[k]} onChange={setJgzt(k)} rows={2} />
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>Team Members</div>
            <button onClick={addMember} className="adm-btn adm-btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}>+ Add Member</button>
          </div>
          <div className="adm-form-group" style={{ marginBottom: 12 }}>
            <label className="adm-label">Section Heading</label>
            <input className="adm-input" value={ct.team.heading} onChange={setTeamHeading} />
          </div>
          {ct.team.members.map((m, idx) => (
            <div key={m.id || idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{m.name || '(new member)'}</div>
                <button onClick={() => removeMember(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>✕ Remove</button>
              </div>
              <div className="adm-form" style={{ gap: 10 }}>
                <div className="adm-form-row">
                  <div className="adm-form-group">
                    <label className="adm-label">Name</label>
                    <input className="adm-input" value={m.name} onChange={setMember(idx, 'name')} />
                  </div>
                  <div className="adm-form-group">
                    <label className="adm-label">Role / Title</label>
                    <input className="adm-input" value={m.role} onChange={setMember(idx, 'role')} />
                  </div>
                </div>
                <div className="adm-form-row">
                  <div className="adm-form-group">
                    <label className="adm-label">Initials (shown in avatar)</label>
                    <input className="adm-input" value={m.initials} onChange={setMember(idx, 'initials')} placeholder="e.g. RKB" style={{ maxWidth: 120 }} />
                  </div>
                  <div className="adm-form-group">
                    <label className="adm-label">Card Background Colour</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="color" value={m.cardBg} onChange={setMember(idx, 'cardBg')} style={{ height: 36, width: 60, borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer' }} />
                      <input className="adm-input" value={m.cardBg} onChange={setMember(idx, 'cardBg')} style={{ maxWidth: 110 }} placeholder="#1a4b7a" />
                    </div>
                  </div>
                </div>
                <div className="adm-form-group">
                  <label className="adm-label">Bio</label>
                  <textarea className="adm-input adm-textarea" value={m.bio} onChange={setMember(idx, 'bio')} rows={3} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          6. HOME YOUTUBE VIDEOS
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="adm-settings-card" style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="adm-settings-card-title" style={{ margin: 0 }}>▶ Home YouTube Videos (3 Slots)</h3>
          <button onClick={saveVideos} className="adm-btn adm-btn-primary" style={{ fontSize: 13 }}>Save Videos</button>
        </div>
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 12 }}>
            Add exactly 3 YouTube links for the home page section.
          </div>
          {videos.slice(0, 3).map((video, idx) => (
            <div key={video.id || idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, color: '#111827', fontSize: 13, marginBottom: 8 }}>Video {idx + 1}</div>
              <div className="adm-form" style={{ gap: 10 }}>
                <div className="adm-form-group">
                  <label className="adm-label">Title</label>
                  <input
                    className="adm-input"
                    value={video.title || ''}
                    onChange={setVideoField(idx, 'title')}
                    placeholder={`Video ${idx + 1}`}
                  />
                </div>
                <div className="adm-form-group">
                  <label className="adm-label">YouTube URL</label>
                  <input
                    className="adm-input"
                    value={video.url || ''}
                    onChange={setVideoField(idx, 'url')}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  {getYouTubeThumb(video.url) && (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={getYouTubeThumb(video.url)}
                        alt={`Preview video ${idx + 1}`}
                        style={{ width: '100%', maxWidth: 240, borderRadius: 6, border: '1px solid #e5e7eb', display: 'block' }}
                      />
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>✓ Video preview</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          7. CONTACT CTA + NEWSLETTER
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="adm-settings-card" style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="adm-settings-card-title" style={{ margin: 0 }}>📧 Newsletter &amp; CTA Section</h3>
          <button onClick={saveCta} className="adm-btn adm-btn-primary" style={{ fontSize: 13 }}>Save</button>
        </div>

        {/* Newsletter */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 12 }}>Newsletter Block</div>
          <div className="adm-form" style={{ gap: 12 }}>
            <div className="adm-form-group">
              <label className="adm-label">Heading</label>
              <input className="adm-input" value={cta.newsletter.heading} onChange={setNews('heading')} />
            </div>
            <div className="adm-form-row">
              <div className="adm-form-group">
                <label className="adm-label">Input Placeholder</label>
                <input className="adm-input" value={cta.newsletter.placeholder} onChange={setNews('placeholder')} />
              </div>
              <div className="adm-form-group">
                <label className="adm-label">Button Text</label>
                <input className="adm-input" value={cta.newsletter.btnText} onChange={setNews('btnText')} />
              </div>
            </div>
            <div className="adm-form-group">
              <label className="adm-label">Success Message (after subscribe)</label>
              <input className="adm-input" value={cta.newsletter.successMsg} onChange={setNews('successMsg')} />
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 12 }}>Final CTA Banner</div>
          <div className="adm-form" style={{ gap: 12 }}>
            <div className="adm-form-group">
              <label className="adm-label">Heading</label>
              <textarea className="adm-input adm-textarea" value={cta.cta.heading} onChange={setCtaF('heading')} rows={2} />
            </div>
            <div className="adm-form-row">
              <div className="adm-form-group">
                <label className="adm-label">Button Text</label>
                <input className="adm-input" value={cta.cta.btnText} onChange={setCtaF('btnText')} />
              </div>
              <div className="adm-form-group">
                <label className="adm-label">Button Link</label>
                <input className="adm-input" value={cta.cta.btnLink} onChange={setCtaF('btnLink')} placeholder="/products" />
              </div>
            </div>
            <ImageUploadField
              label="Product Image"
              value={cta.cta.image}
              onChange={setCtaImg}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeAdmin;
